import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../../frontend/components/Footer";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  handleGetKitchenOfflineWindows,
  handleAddKitchenOfflineWindow,
  handleDeleteKitchenOfflineWindow,
} from "../../services/shef";

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const to12Hour = (time) => {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
};

const formatWindow = (w) => {
  const day = DAYS[w.day_of_week] ?? "Unknown";
  if (w.all_day) return `${day} — All day`;
  return `${day} — ${to12Hour(w.start_time)} to ${to12Hour(w.end_time)}`;
};

export const KitchenOffline = () => {
  const { authToken } = useSelector((state) => state.user);
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [form, setForm] = useState({
    day_of_week: "",
    all_day: false,
    start_time: "",
    end_time: "",
  });

  const fetchWindows = async () => {
    try {
      setLoading(true);
      const data = await handleGetKitchenOfflineWindows(authToken);
      setWindows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load kitchen offline windows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) fetchWindows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const overnight = useMemo(() => {
    if (form.all_day || !form.start_time || !form.end_time) return false;
    return form.end_time <= form.start_time;
  }, [form.all_day, form.start_time, form.end_time]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.day_of_week === "") {
      toast.error("Please select a day");
      return;
    }
    if (!form.all_day && (!form.start_time || !form.end_time)) {
      toast.error("Please select a start and end time");
      return;
    }
    try {
      setSaving(true);
      const res = await handleAddKitchenOfflineWindow(authToken, {
        day_of_week: parseInt(form.day_of_week, 10),
        all_day: form.all_day,
        start_time: form.all_day ? null : form.start_time,
        end_time: form.all_day ? null : form.end_time,
      });
      toast.success(res.message || "Kitchen offline window added");
      setForm({ day_of_week: "", all_day: false, start_time: "", end_time: "" });
      await fetchWindows();
    } catch (error) {
      toast.error(error.message || "Failed to add kitchen offline window");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this kitchen offline window?")) return;
    try {
      setDeletingId(id);
      const res = await handleDeleteKitchenOfflineWindow(authToken, id);
      toast.success(res.message || "Kitchen offline window removed");
      setWindows((prev) => prev.filter((w) => w.id !== id));
    } catch (error) {
      toast.error(error.message || "Failed to remove kitchen offline window");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Kitchen Offline</h1>
        <p className="text-gray-600 mb-8">
          Set the days and times when your kitchen is offline. Customers will not
          be able to place orders whose delivery slot falls inside these windows.
        </p>

        {/* Add window form */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            Add Offline Window
          </h2>
          <form
            onSubmit={onSubmit}
            className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Day
              </label>
              <select
                value={form.day_of_week}
                onChange={(e) =>
                  setForm((p) => ({ ...p, day_of_week: e.target.value }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all"
              >
                <option value="">Select Day</option>
                {DAYS.map((d, i) => (
                  <option key={d} value={i}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From
              </label>
              <input
                type="time"
                value={form.start_time}
                disabled={form.all_day}
                onChange={(e) =>
                  setForm((p) => ({ ...p, start_time: e.target.value }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To
              </label>
              <input
                type="time"
                value={form.end_time}
                disabled={form.all_day}
                onChange={(e) =>
                  setForm((p) => ({ ...p, end_time: e.target.value }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all disabled:bg-gray-100"
              />
            </div>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={form.all_day}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      all_day: e.target.checked,
                      start_time: "",
                      end_time: "",
                    }))
                  }
                  className="w-4 h-4 accent-red-600"
                />
                All day
              </label>
              <button
                type="submit"
                disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-300 shadow-md hover:shadow-lg disabled:opacity-50"
              >
                {saving ? "Adding..." : "Add Window"}
              </button>
            </div>
          </form>
          {overnight && (
            <p className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
              This window crosses midnight — the kitchen will be offline from{" "}
              {DAYS[form.day_of_week]} {to12Hour(form.start_time)} until{" "}
              {DAYS[(parseInt(form.day_of_week, 10) + 1) % 7]}{" "}
              {to12Hour(form.end_time)}.
            </p>
          )}
        </div>

        {/* Existing windows */}
        <div className="bg-white rounded-xl shadow-md p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            Scheduled Offline Windows
          </h2>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : windows.length === 0 ? (
            <p className="text-gray-500">
              No offline windows set. Your kitchen can receive orders for any
              delivery slot.
            </p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {windows.map((w) => (
                <li
                  key={w.id}
                  className="flex items-center justify-between py-4"
                >
                  <span className="text-gray-800 font-medium">
                    {formatWindow(w)}
                  </span>
                  <button
                    onClick={() => onDelete(w.id)}
                    disabled={deletingId === w.id}
                    className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                  >
                    {deletingId === w.id ? "Removing..." : "Remove"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default KitchenOffline;
